import { intersection, isEqual, uniq, uniqBy } from 'lodash';
import { DodFactSet, DodFilter, DodFilters, DodProductSubDimension, DodRunConfig } from '@/types/DodRun';
import {
    DodFilterType,
    DodProductFilterTypes,
    DodProductParentFilterTypes,
    DodTimePeriodFilterTypes,
    RowColConfig,
} from '@/components/DodConfigEditor/types';
import { alert } from '@/components/form/ByzzerModal';
import { DodSelectionPreviewNode } from './DodFilterPreview';
import {
    DodDeletedExtractsResponse,
    DodPresetSummary,
    DodPresetType,
    DodRecommendedSelectionsType,
    RecommendedSelections,
    RecommendedSelectionsMap,
    RecommendedSelectionsSummary,
} from '@/types/ApiTypes';
import moment from 'moment';
import { format } from 'date-fns-tz';
import { TimePeriodRange } from '@/utils/timePeriod/TimePeriodRange';

export function filterValuesToStrings(filter?: DodFilter<string[] | 'all'>, defaultValue: string[] = []): string[] {
    if (!filter) return defaultValue;

    const { values, summedSelections } = filter;
    if (values === 'all' || summedSelections.some((ss) => ss.values === 'all')) {
        return defaultValue;
    }

    return uniq([
        ...valuesToStrings(values),
        ...summedSelections.map((sum) => valuesToStrings(sum.values)).flat(), // returns an array of arrays
    ]);
}

export function hasComma(value: string): boolean {
    return value.includes(',');
}

export function hasSpecialCharacters(value: string): boolean {
    const specialCharsRegex = /[^a-zA-Z0-9\s_]/;
    return specialCharsRegex.test(value);
}

export function isSelectedMarketNotAccessible(runconfig: DodRunConfig): boolean {
    const { values, summedSelections } = runconfig.filters.markets;
    const allValues = [...values, ...summedSelections.map((ss) => ss.values)].flat();
    const count = uniqBy(allValues, 'key').length;
    return count < 1;
}

export function alertSelectionLimitExceed(displayTabName: string, limit?: number) {
    return alert({
        content: `As a free user you can only select ${limit} ${displayTabName}. To see more, reach out to your account executive to upgrade your subscription.`,
    });
}

export function valuesToStrings(values: string[] | 'all', defaultValue: string[] = []): string[] {
    if (Array.isArray(values)) {
        return values;
    } else {
        return defaultValue;
    }
}

export function filterHasExplicitValues(filter: DodFilter<string[] | 'all'> | DodFilter): boolean {
    if (hasArrayValues(filter.values)) {
        return true;
    }
    if (filter.summedSelections.some((sum) => hasArrayValues(sum.values))) {
        return true;
    }

    return false;
}

export function filterHasValues(filter: DodFilter<string[] | 'all'> | DodFilter): boolean {
    if (
        (Array.isArray(filter.values) && filter.values.length) ||
        filter.values === 'all' ||
        filter.summedSelections.length
    ) {
        return true;
    }
    return false;
}

export function hasArrayValues(values: string[] | 'all'): boolean {
    return Boolean(Array.isArray(values) && values.length);
}

export function getKeyByValue(object, value) {
    return Object.keys(object).find((key) => object[key] === value);
}

export function isCharacteristicFilter(filterType: DodFilterType): boolean {
    return ['characteristics', 'customCharacteristics', 'marketCharacteristics'].includes(filterType.type);
}

export function isPpgFilter(filterType: DodFilterType): boolean {
    return ['ppgs'].includes(filterType.type);
}

export function isMarketResetRequired(filterType: DodFilterType, value: DodFilters): boolean {
    // todo: add logic to determine if selected markets will be impacted
    return (
        filterType.type === 'categories' &&
        Boolean(value.markets.values.length || value.markets.summedSelections.length)
    );
}

export function isTimePeriodFilterTypes(filterType: string): boolean {
    return Object.values(DodTimePeriodFilterTypes).includes(filterType as DodTimePeriodFilterTypes);
}

export function isProductFilterTypes(filterType: string): boolean {
    return Object.values(DodProductFilterTypes).includes(filterType as DodProductFilterTypes);
}

export function isParentProductFilterTypes(filterType: string): boolean {
    return Object.values(DodProductParentFilterTypes).includes(filterType as DodProductParentFilterTypes);
}

export function isCharacteristic(type: string): boolean {
    return ['characteristics', 'customCharacteristics'].includes(type);
}

export const defaultProductOrder: DodProductSubDimension[] = [
    'departments',
    'superCategories',
    'categories',
    'subcategories',
    'parentCompanies',
    'manufacturers',
    'brands',
    'upcs',
    'productDescriptions',
];

export const DodFactSuffixes = {
    yearAgo: ` YA`,
    changeYearAgo: ` Change vs Year-Ago`,
    perChangeYearAgo: ` % Change vs Year-Ago`,
    twoYearAgo: ` 2 Year Ago`,
    changeTwoYearAgo: ` Change vs 2 Year-Ago`,
    perChangeTwoYearAgo: ` % Change vs 2 Year-Ago`,
    threeYearAgo: ` 3 Year Ago`,
    changeThreeYearAgo: ` Change vs 3 Year-Ago`,
    perChangeThreeYearAgo: ` % Change vs 3 Year-Ago`,
    fourYearAgo: ` 4 Year Ago`,
    changeFourYearAgo: ` Change vs 4 Year-Ago`,
    perChangeFourYearAgo: ` % Change vs 4 Year-Ago`,
    shareByCategory: ` % Share By Category`,
    shareByCategoryYearAgo: ` % Share By Category Year-Ago`,
    shareByCategoryTwoYearAgo: ` % Share By Category 2 Year-Ago`,
    shareByCategoryThreeYearAgo: ` % Share By Category 3 Year-Ago`,
    shareByCategoryFourYearAgo: ` % Share By Category 4 Year-Ago`,
    shareByCategorySharePointChangeYearAgo: ` % Share By Category Share Point Change Year-Ago`,
    shareByCategorySharePointChangeTwoYearAgo: ` % Share By Category Share Point Change 2 Year-Ago`,
    shareByCategorySharePointChangeThreeYearAgo: ` % Share By Category Share Point Change 3 Year-Ago`,
    shareByCategorySharePointChangeFourYearAgo: ` % Share By Category Share Point Change 4 Year-Ago`,
    shareByBrand: ` % Share By Brand`,
    shareByBrandYearAgo: ` % Share By Brand Year-Ago`,
    shareByBrandTwoYearAgo: ` % Share By Brand 2 Year-Ago`,
    shareByBrandThreeYearAgo: ` % Share By Brand 3 Year-Ago`,
    shareByBrandFourYearAgo: ` % Share By Brand 4 Year-Ago`,
    shareByBrandSharePointChangeYearAgo: ` % Share By Brand Share Point Change Year-Ago`,
    shareByBrandSharePointChangeTwoYearAgo: ` % Share By Brand Share Point Change 2 Year-Ago`,
    shareByBrandSharePointChangeThreeYearAgo: ` % Share By Brand Share Point Change 3 Year-Ago`,
    shareByBrandSharePointChangeFourYearAgo: ` % Share By Brand Share Point Change 4 Year-Ago`,
    shareByProductSelections: ` % Share By Product Selections`,
    shareByProductSelectionsYearAgo: ` % Share By Product Selections Year-Ago`,
    shareByProductSelectionsTwoYearAgo: ` % Share By Product Selections 2 Year-Ago`,
    shareByProductSelectionsThreeYearAgo: ` % Share By Product Selections 3 Year-Ago`,
    shareByProductSelectionsFourYearAgo: ` % Share By Product Selections 4 Year-Ago`,
    shareByProductSelectionsSharePointChangeYearAgo: ` % Share By Product Selections Share Point Change Year-Ago`,
    shareByProductSelectionsSharePointChangeTwoYearAgo: ` % Share By Product Selections Share Point Change 2 Year-Ago`,
    shareByProductSelectionsSharePointChangeThreeYearAgo: ` % Share By Product Selections Share Point Change 3 Year-Ago`,
    shareByProductSelectionsSharePointChangeFourYearAgo: ` % Share By Product Selections Share Point Change 4 Year-Ago`,
    shareBySubcategory: ` % Share By Subcategory`,
    shareBySubcategoryYearsAgo: ` % Share By Subcategory Year-Ago`,
    shareBySubcategoryTwoYearsAgo: ` % Share By Subcategory 2 Year-Ago`,
    shareBySubcategoryThreeYearsAgo: ` % Share By Subcategory 3 Year-Ago`,
    shareBySubcategoryFourYearsAgo: ` % Share By Subcategory 4 Year-Ago`,
    shareBySubcategorySharePointChangeYearAgo: ` % Share By Subcategory Share Point Change Year-Ago`,
    shareBySubcategorySharePointChangeTwoYearAgo: ` % Share By Subcategory Share Point Change 2 Year-Ago`,
    shareBySubcategorySharePointChangeThreeYearAgo: ` % Share By Subcategory Share Point Change 3 Year-Ago`,
    shareBySubcategorySharePointChangeFourYearAgo: ` % Share By Subcategory Share Point Change 4 Year-Ago`,
};

export function factSetToDisplayNames(factSet: DodFactSet): string[] {
    return [...factSetToCoreDisplayNames(factSet), ...factSetToSharedDisplayNames(factSet)];
}

export function factSetToCoreDisplayNames(factSet: DodFactSet): string[] {
    const { fact: currentPeriod, yearAgo, changeVsYearAgo, percentChangeVsYearAgo, twoYearAgo, threeYearAgo, fourYearAgo, changeVsTwoYearAgo, changeVsThreeYearAgo, changeVsFourYearAgo, percentChangeVsFourYearAgo, percentChangeVsThreeYearAgo, percentChangeVsTwoYearAgo } = factSet;

    const values: string[] = [];
    if (currentPeriod) {
        values.push(factSet.display);
    }
    if (yearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.yearAgo}`);
    }
    if (changeVsYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.changeYearAgo}`);
    }
    if (percentChangeVsYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.perChangeYearAgo}`);
    }
    if (twoYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.twoYearAgo}`)
    }
    if (changeVsTwoYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.changeTwoYearAgo}`);
    }
    if (percentChangeVsTwoYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.perChangeTwoYearAgo}`);
    }
    if (threeYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.threeYearAgo}`)
    }
    if (changeVsThreeYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.changeThreeYearAgo}`);
    }
    if (percentChangeVsThreeYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.perChangeThreeYearAgo}`);
    }
    if (fourYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.fourYearAgo}`)
    }
    if (changeVsFourYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.changeFourYearAgo}`);
    }
    if (percentChangeVsFourYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.perChangeFourYearAgo}`);
    }

    return values;
}

export function factSetToSharedDisplayNames(factSet: DodFactSet): string[] {
    const {
        category,
        categoryYearAgo,
        categoryTwoYearAgo,
        categoryThreeYearAgo,
        categoryFourYearAgo,
        categorySharePointChangeYearAgo,
        categorySharePointChangeTwoYearAgo,
        categorySharePointChangeThreeYearAgo,
        categorySharePointChangeFourYearAgo,
        brand,
        brandYearAgo,
        brandTwoYearAgo,
        brandThreeYearAgo,
        brandFourYearAgo,
        brandSharePointChangeYearAgo,
        brandSharePointChangeTwoYearAgo,
        brandSharePointChangeThreeYearAgo,
        brandSharePointChangeFourYearAgo,
        productSelections,
        productSelectionsYearAgo,
        productSelectionsTwoYearAgo,
        productSelectionsThreeYearAgo,
        productSelectionsFourYearAgo,
        productSelectionsSharePointChangeYearAgo,
        productSelectionsSharePointChangeTwoYearAgo,
        productSelectionsSharePointChangeThreeYearAgo,
        productSelectionsSharePointChangeFourYearAgo,
        subCategory,
        subCategoryYearAgo,
        subCategoryTwoYearAgo,
        subCategoryThreeYearAgo,
        subCategoryFourYearAgo,
        subCategorySharePointChangeYearAgo,
        subCategorySharePointChangeTwoYearAgo,
        subCategorySharePointChangeThreeYearAgo,
        subCategorySharePointChangeFourYearAgo,
    } = factSet;
    const values: string[] = [];

    if (category) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByCategory}`);
    }
    if (categoryYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByCategoryYearAgo}`);
    }
    if (categoryTwoYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByCategoryTwoYearAgo}`);
    }
    if (categoryThreeYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByCategoryThreeYearAgo}`);
    }
    if (categoryFourYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByCategoryFourYearAgo}`);
    }
    if (categorySharePointChangeYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByCategorySharePointChangeYearAgo}`);
    }
    if (categorySharePointChangeTwoYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByCategorySharePointChangeTwoYearAgo}`);
    }
    if (categorySharePointChangeThreeYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByCategorySharePointChangeThreeYearAgo}`);
    }
    if (categorySharePointChangeFourYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByCategorySharePointChangeFourYearAgo}`);
    }
    if (brand) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByBrand}`);
    }
    if (brandYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByBrandYearAgo}`);
    }
    if (brandTwoYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByBrandTwoYearAgo}`);
    }
    if (brandThreeYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByBrandThreeYearAgo}`);
    }
    if (brandFourYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByBrandFourYearAgo}`);
    }
    if (brandSharePointChangeYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByBrandSharePointChangeYearAgo}`);
    }
    if (brandSharePointChangeTwoYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByBrandSharePointChangeTwoYearAgo}`);
    }
    if (brandSharePointChangeThreeYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByBrandSharePointChangeThreeYearAgo}`);
    }
    if (brandSharePointChangeFourYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByBrandSharePointChangeFourYearAgo}`);
    }
    if (productSelections) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByProductSelections}`);
    }
    if (productSelectionsYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByProductSelectionsYearAgo}`);
    }
    if (productSelectionsTwoYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByProductSelectionsTwoYearAgo}`);
    }
    if (productSelectionsThreeYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByProductSelectionsThreeYearAgo}`);
    }
    if (productSelectionsFourYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByProductSelectionsFourYearAgo}`);
    }
    if (productSelectionsSharePointChangeYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByProductSelectionsSharePointChangeYearAgo}`);
    }
    if (productSelectionsSharePointChangeTwoYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByProductSelectionsSharePointChangeTwoYearAgo}`);
    }
    if (productSelectionsSharePointChangeThreeYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByProductSelectionsSharePointChangeThreeYearAgo}`);
    }
    if (productSelectionsSharePointChangeFourYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareByProductSelectionsSharePointChangeFourYearAgo}`);
    }
    if (subCategory) {
        values.push(`${factSet.display}${DodFactSuffixes.shareBySubcategory}`)
    }
    if (subCategoryYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareBySubcategoryYearsAgo}`)
    }
    if (subCategoryTwoYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareBySubcategoryTwoYearsAgo}`)
    }
    if (subCategoryThreeYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareBySubcategoryThreeYearsAgo}`)
    }
    if (subCategoryFourYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareBySubcategoryFourYearsAgo}`)
    }
    if (subCategorySharePointChangeYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareBySubcategorySharePointChangeYearAgo}`)
    }
    if (subCategorySharePointChangeTwoYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareBySubcategorySharePointChangeTwoYearAgo}`)
    }
    if (subCategorySharePointChangeThreeYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareBySubcategorySharePointChangeThreeYearAgo}`)
    }
    if (subCategorySharePointChangeFourYearAgo) {
        values.push(`${factSet.display}${DodFactSuffixes.shareBySubcategorySharePointChangeFourYearAgo}`)
    }

    return values;
}

type AutoConditionNameInput = {
    conditions?: DodRunConfig['conditions'];
    filters?: {
        markets?: {
            values?: Array<{ name?: string; key?: string }>;
            summedSelections?: Array<{ name?: string }>;
        };
        timePeriods?: {
            values?: DodRunConfig['filters']['timePeriods']['values'];
            summedSelections?: Array<{ name?: string }>;
        };
    };
};

export function getAutoConditionName(runConfig: AutoConditionNameInput, maxDate?: string): string {
    const COMPARATOR_LABELS: Record<string, string> = {
        gt: '>',
        lt: '<',
        eq: '=',
        gte: '≥',
        lte: '≤',
        neq: '≠',
        btw: 'between',
        nbtw: 'not between',
        after: 'after',
        before: 'before',
    };

    const conditionParts = (runConfig.conditions?.facts ?? [])
        .filter((c) => c.fact && c.comparator && c.value)
        .map((c, i) => {
            const prefix = i > 0 ? ` ${c.condition.toUpperCase()} ` : '';
            const displayValue = Array.isArray(c.value)
                ? `${c.value[0]} and ${c.value[1]}`
                : c.value;
            return `${prefix}${c.fact} ${COMPARATOR_LABELS[c.comparator] ?? c.comparator} ${displayValue}`;
        })
        .join('');

    const marketOptions = [
        { display: 'Each Market', value: 'each_market' },
        ...(runConfig.filters?.markets?.values ?? [])
            .filter((m) => m.name)
            .map((m) => ({ display: m.name, value: m.key ?? m.name })),
        ...(runConfig.filters?.markets?.summedSelections ?? [])
            .filter((s) => s.name)
            .map((s) => ({ display: s.name, value: s.name })),
    ];

    const timePeriodOptions = [
        { display: 'Each Time Period', value: 'each_time_period' },
        ...(runConfig.filters?.timePeriods?.values ?? [])
            .filter((tp): tp is string => typeof tp === 'string')
            .map((tp) => {
                try {
                    return { display: new TimePeriodRange(tp, maxDate).toLegacyDodString(), value: tp };
                } catch {
                    return { display: tp, value: tp };
                }
            }),
        ...(runConfig.filters?.timePeriods?.summedSelections ?? [])
            .filter((s) => s.name)
            .map((s) => ({ display: s.name, value: s.name })),
    ];

    const conditionMarket = runConfig.conditions?.market;
    const marketText = (!conditionMarket || conditionMarket === 'each_market')
        ? 'Each Market'
        : marketOptions.find((o) => o.value === conditionMarket)?.display ?? conditionMarket;

    const conditionTimePeriod = runConfig.conditions?.timePeriod;
    const timePeriodText = (!conditionTimePeriod || conditionTimePeriod === 'each_time_period')
        ? 'Each Time Period'
        : timePeriodOptions.find((o) => o.value === conditionTimePeriod)?.display ?? conditionTimePeriod;

    const products = runConfig.conditions?.products;
    const productMode = products?.mode ?? 'All';
    const hasProductFilter = productMode !== 'All' && Boolean(products?.count) && Boolean(products?.sortByFact);

    if (!conditionParts && !hasProductFilter) return '';

    if (!conditionParts) {
        return `${productMode} ${products!.count} Products Based on ${products!.sortByFact} in ${marketText} and ${timePeriodText}`;
    }

    if (hasProductFilter) {
        return `${productMode} ${products!.count} Products where ${conditionParts} in ${marketText} and ${timePeriodText}`;
    }

    return `All Products where ${conditionParts} in ${marketText} and ${timePeriodText}`;
}

export function getConditionNamePreservingCustom(
    previousRunConfig: AutoConditionNameInput,
    nextRunConfig: AutoConditionNameInput,
    maxDate?: string
): string {
    const previousName = previousRunConfig.conditions?.name;
    const previousAutoName = getAutoConditionName(previousRunConfig, maxDate);

    if (previousName && previousName !== previousAutoName) {
        return previousName;
    }

    return getAutoConditionName(nextRunConfig, maxDate);
}

export const DodRunMethod = {
    create: 'new run',
    edit: 'rerun',
    modifiedRun: 'modified run',
    layout: 'apply new layout run',
    ScheduledRun: 'scheduled run',
};

export const compareObjects = (obj1, obj2, parentKey = '') => {
    try {
        let diffs: string[] = [];

        const keys1 = obj1 ? Object.keys(obj1) : [];
        const keys2 = obj2 ? Object.keys(obj2) : [];

        // Check keys that are only in obj1
        keys1.forEach((key) => {
            const fullKey = parentKey ? `${parentKey}.${key}` : key;

            if (!keys2.includes(key)) {
                // Only consider keys missing in obj2 if their value in obj1 is not an empty array or undefined
                if (Array.isArray(obj1[key]) && obj1[key].length === 0) {
                    return;
                }
                if (obj1[key] !== undefined) {
                    diffs.push(`Key '${fullKey}' is missing in the second configuration.`);
                }
            } else if (typeof obj1[key] === 'object' && obj1[key] !== null) {
                // Recursively compare objects
                diffs = diffs.concat(compareObjects(obj1[key], obj2[key], fullKey));
            } else if (obj1[key] !== obj2[key]) {
                // Consider undefined or empty arrays as equivalent to missing keys
                if (
                    (Array.isArray(obj1[key]) && obj1[key].length === 0 && obj2[key] === undefined) ||
                    (obj1[key] === undefined && Array.isArray(obj2[key]) && obj2[key].length === 0)
                ) {
                    return; // Treat as equal
                }
                diffs.push(`Value of '${fullKey}' differs: '${obj1[key]}' vs '${obj2[key]}'.`);
            }
        });

        // Check keys that are only in obj2
        keys2.forEach((key) => {
            const fullKey = parentKey ? `${parentKey}.${key}` : key;

            if (!keys1.includes(key)) {
                // Only consider keys missing in obj1 if their value in obj2 is not an empty array or undefined
                if (Array.isArray(obj2[key]) && obj2[key].length === 0) {
                    return;
                }
                if (obj2[key] !== undefined) {
                    diffs.push(`Key '${fullKey}' is missing in the first configuration.`);
                }
            }
        });

        return diffs;
    } catch (err) {
        console.error('Error comparing objects', err);
        throw err;
    }
};

export const marketFolderLabelMapping = {
    'Major Market': 'majorMarkets',
    Division: 'divisions',
    Stateline: 'stateLines',
    Region: 'regions',
    Channel: 'channels',
    Account: 'accounts',
};

export const invertedMarketFolderLabelMapping = Object.fromEntries(
    Object.entries(marketFolderLabelMapping).map(([key, value]) => [value, key])
);

const SHARE_FACT_KEYS: (keyof DodFactSet)[] = [
    'category',
    'categoryYearAgo',
    'categoryTwoYearAgo',
    'categoryThreeYearAgo',
    'categoryFourYearAgo',
    'categorySharePointChangeYearAgo',
    'categorySharePointChangeTwoYearAgo',
    'categorySharePointChangeThreeYearAgo',
    'categorySharePointChangeFourYearAgo',
    'brand',
    'brandYearAgo',
    'brandTwoYearAgo',
    'brandThreeYearAgo',
    'brandFourYearAgo',
    'brandSharePointChangeYearAgo',
    'brandSharePointChangeTwoYearAgo',
    'brandSharePointChangeThreeYearAgo',
    'brandSharePointChangeFourYearAgo',
    'productSelections',
    'productSelectionsYearAgo',
    'productSelectionsTwoYearAgo',
    'productSelectionsThreeYearAgo',
    'productSelectionsFourYearAgo',
    'productSelectionsSharePointChangeYearAgo',
    'productSelectionsSharePointChangeTwoYearAgo',
    'productSelectionsSharePointChangeThreeYearAgo',
    'productSelectionsSharePointChangeFourYearAgo',
    'subCategory',
    'subCategoryYearAgo',
    'subCategoryTwoYearAgo',
    'subCategoryThreeYearAgo',
    'subCategoryFourYearAgo',
    'subCategorySharePointChangeYearAgo',
    'subCategorySharePointChangeTwoYearAgo',
    'subCategorySharePointChangeThreeYearAgo',
    'subCategorySharePointChangeFourYearAgo',
];

const SHARE_OF_BRAND_FACT_KEYS: (keyof DodFactSet)[] = [
    'brand', 'brandYearAgo', 'brandTwoYearAgo', 'brandThreeYearAgo', 'brandFourYearAgo', 'brandSharePointChangeYearAgo', 'brandSharePointChangeTwoYearAgo', 'brandSharePointChangeThreeYearAgo', 'brandSharePointChangeFourYearAgo'
];

const SHARE_OF_CATEGORY_FACT_KEYS: (keyof DodFactSet)[] = [
    'category', 'categoryYearAgo', 'categoryTwoYearAgo', 'categoryThreeYearAgo', 'categoryFourYearAgo', 'categorySharePointChangeYearAgo', 'categorySharePointChangeTwoYearAgo', 'categorySharePointChangeThreeYearAgo', 'categorySharePointChangeFourYearAgo'
];

const SHARE_OF_SUBCATEGORY_FACT_KEYS: (keyof DodFactSet)[] = [
    'subCategory', 'subCategoryYearAgo', 'subCategoryTwoYearAgo', 'subCategoryThreeYearAgo', 'subCategoryFourYearAgo', 'subCategorySharePointChangeYearAgo', 'subCategorySharePointChangeTwoYearAgo', 'subCategorySharePointChangeThreeYearAgo', 'subCategorySharePointChangeFourYearAgo'
];

function factHasAnyKey(fact: DodFactSet, keys: (keyof DodFactSet)[]): boolean {
    return keys.some((key) => fact[key] === true);
}

export function hasShareOfBrandFacts(facts: DodFactSet[]): boolean {
    return facts.some((fact) => factHasAnyKey(fact, SHARE_OF_BRAND_FACT_KEYS));
}

export function getProductDimensionHideRestriction(
    dim: string,
    facts: DodFactSet[],
    options: { isBrandSelected?: boolean } = {}
): { disable: boolean; tip: string } {
    const { isBrandSelected = false } = options;

    for (const fact of facts) {
        if (dim === 'categories' && factHasAnyKey(fact, SHARE_OF_CATEGORY_FACT_KEYS)) {
            return {
                disable: true,
                tip: 'You cannot hide Category when you have Share of Category facts selected.',
            };
        }
        if (dim === 'brands' && factHasAnyKey(fact, SHARE_OF_BRAND_FACT_KEYS)) {
            return {
                disable: true,
                tip: 'You cannot hide Brand when you have Share of Brand facts selected.',
            };
        }
        if (dim === 'upcs' && fact.isUPCrequired) {
            return {
                disable: true,
                tip: 'You cannot hide UPC when you have UPC-level facts selected.',
            };
        }
        if (dim === 'subcategories' && factHasAnyKey(fact, SHARE_OF_SUBCATEGORY_FACT_KEYS)) {
            return {
                disable: true,
                tip: 'You cannot hide Subcategory when you have Share of Subcategory facts selected.',
            };
        }
    }

    if (dim === 'upcs' && hasShareOfBrandFacts(facts) && !isBrandSelected) {
        return {
            disable: true,
            tip: 'You cannot hide UPC when you have Share of Brand facts selected without the Brand field.',
        };
    }

    return { disable: false, tip: '' };
}

/**
 * Checks if any fact in the list has a share-type column (category, brand, subCategory, productSelections)
 * or isUPCrequired set to true.
 */
export const hasUPCorSharedFactSelected = (facts: DodFactSet[]): boolean => {
    return facts.some((fact) =>
        fact.isUPCrequired || SHARE_FACT_KEYS.some((key) => fact[key] === true)
    );
};

// export const weeksSellingFactIds: number[] = [1031];
// export const cdiBdiFactIds: number[] = [1021, 1067, 1068, 1069, 1071, 1072];
// setting both arrays to empty for now as per the new requirement to remove the restriction on these facts with new data changes. 
// If we need to re-enable the restriction, we can uncomment the above lines and use them, else all relevant code can be cleaned up on stable build.
export const weeksSellingFactIds: number[] = [];
export const cdiBdiFactIds: number[] = [];
export const isWorkInProgressFact = (selectedFacts: DodFactSet[]): { disabled: boolean; hoverText: string } => {
    return {
        disabled: selectedFacts.some((fact) => [...weeksSellingFactIds, ...cdiBdiFactIds].includes(fact.id)),
        hoverText:
            'This selection type is not compatible with one of your fact selections. If you wish to include this type of selection in your run, you will need to remove any CDI/BDI and Number of Weeks Selling facts that were included.',
    };
};

// disabling conditions for Weeks selling fact - BYZ-13989
// disabling conditions for CDI/BDI fact - BYZ-13988
export const isWorkInProgressFactsDisabled = ({ fact, runConfig, rowColConfigs }: {
    fact: DodFactSet,
    runConfig: DodRunConfig,
    rowColConfigs: RowColConfig[]
}): boolean => {
    return (
        (isWeeksSellingFact(fact) || isCdiBdiFact(fact)) &&
        (isSubTotalSelected(runConfig) ||
            isCategoriesTotalSelected(runConfig) ||
            isDisabledDueToCategoryHidden(rowColConfigs) ||
            isDisabledDueToStackedProducts(rowColConfigs) ||
            // isDisabledDueToRestrictedProducts(runConfig.filters) ||
            // isSummedProductSelected(runConfig.filters) ||
            isSummedMarketSelected(runConfig.filters))
            // isSummedTimePeriodsSelected(runConfig.filters))
    );
};

export const isCategoryFactStacked = (rowColConfig: RowColConfig[], flag?: String): boolean => {
    // Check if the fact has any of the category-related properties
    const hasCategoryFact = flag === 'c' || flag === 'cya' || flag === 'ctya' || flag === 'ctya3' || flag === 'cfya' || flag === 'cspcya' || flag === 'cspctya' || flag === 'cspctya3' || flag === 'cspcfya';

    // Check if rowColConfig has dim === 'categories' and stack === true
    const isStackedCategory = rowColConfig.some((config) => config.dim === 'categories' && config.stack === true);

    // Return true if both conditions are met
    return (hasCategoryFact && isStackedCategory) ?? false;
};

export const isSubCategoryFactStacked = (rowColConfig: RowColConfig[], flag?: String): boolean => {
    // Check if the fact has any of the category-related properties
    const hasSubCategoryFact =
        flag === 'sc' || flag === 'scya' || flag === 'sctya' || flag === 'sctya3' || flag === 'scfya' || flag === 'scspcya' || flag === 'scspctya' || flag === 'scspctya3' || flag === 'scspcfya';

    // Check if rowColConfig has dim === 'categories' and stack === true
    const isStackedSubCategory = rowColConfig.some((config) => config.dim === 'subcategories' && config.stack === true);

    // Return true if both conditions are met
    return (hasSubCategoryFact && isStackedSubCategory) ?? false;
};

export const isBrandFactStacked = (rowColConfig: RowColConfig[], flag?: String): boolean => {
    // Check if the fact has any of the brand-related properties
    const hasBrandFact = flag === 'b' || flag === 'bya' || flag === 'btya' || flag === 'btya3' || flag === 'bfya' || flag === 'bspcya' || flag === 'bspctya' || flag === 'bspctya3' || flag === 'bspcfya';

    // Check if rowColConfig has dim === 'brands' and stack === true
    const isStackedBrand = rowColConfig.some((config) => config.dim === 'brands' && config.stack === true);

    // Return true if both conditions are met
    return (hasBrandFact && isStackedBrand) ?? false;
};

export const isProductSelectionsFactStacked = (
    rowColConfig: RowColConfig[],
    flag?: String
): boolean => {
    // Check if the fact has any of the category-related properties
    const hasShareFact =
        flag === 'b' ||
        flag === 'bya' ||
        flag === 'btya' ||
        flag === 'btya3' ||
        flag === 'bfya' ||
        flag === 'sc' ||
        flag === 'scya' ||
        flag === 'sctya' ||
        flag === 'sctya3' ||
        flag === 'scfya' ||
        flag === 'c' ||
        flag === 'cya' ||
        flag === 'ctya' ||
        flag === 'ctya3' ||
        flag === 'cfya' ||
        flag === 'ps' ||
        flag === 'psya' ||
        flag === 'pstya' ||
        flag === 'pstya3' ||
        flag === 'psfya' || 
        flag === 'psspcya' ||
        flag === 'psspctya' ||
        flag === 'psspctya3' ||
        flag === 'psspcfya';

    const isStackedProduct = rowColConfig.some((config) => config.type === 'products' && config.stack === true);

    return (hasShareFact && isStackedProduct) ?? false;
};

export const isWeeksSellingFact = (fact: DodFactSet) => {
    return weeksSellingFactIds.includes(fact.id);
};

export const isCdiBdiFact = (fact: DodFactSet) => {
    return cdiBdiFactIds.includes(fact.id);
};

const isSubTotalSelected = (runConfig: DodRunConfig) => {
    return runConfig.layout.includeSubTotals;
};

const isCategoriesTotalSelected = (runConfig: DodRunConfig) => {
    return runConfig.layout.includeCategoryTotals;
}

const isSummedMarketSelected = (filters: DodFilters) => {
    return Boolean(filters.markets.summedSelections.length);
};

const isSummedTimePeriodsSelected = (filters: DodFilters) => {
    return Boolean(filters.timePeriods.summedSelections.length);
};

const isDisabledDueToRestrictedProducts = (filters: DodFilters) => {
    const productFilters = Object.keys(filters).filter(
        (filter) => filter === 'characteristics' || filter === 'customCharacteristics' || filter === 'ppgs'
    );
    for (const filter of productFilters) {
        if (Object.keys(filters[filter]).length) {
            return true;
        }
    }
    return false;
}

const isSummedProductSelected = (filters: DodFilters) => {
    let isSummedProductSelected: boolean = false;
    const productFilters = Object.keys(filters).filter((filter) => filter !== 'markets' && filter !== 'timePeriods');
    for (const filterType of productFilters) {
        if (
            [
                DodProductFilterTypes.CHARACTERISTICS,
                DodProductFilterTypes.CUSTOM_CHARACTERISTICS,
                DodProductFilterTypes.PPGS,
            ].includes(filterType as DodProductFilterTypes)
        ) {
            const filterKeys = Object.keys(filters[filterType]);
            for (const key of filterKeys) {
                if (filters[filterType][key].summedSelections.length) {
                    isSummedProductSelected = true;
                }
            }
        } else {
            if (filters[filterType].summedSelections.length) {
                isSummedProductSelected = true;
            }
        }
    }
    return isSummedProductSelected;
};

// disable both weeks selling and cdi/bdi facts if hidden products is present in layout
const isDisabledDueToCategoryHidden = (rowColConfigs: RowColConfig[]) => {
    return rowColConfigs.some((rowColConfig) => rowColConfig.type === 'products' && rowColConfig.hide);
};

// disable both weeks selling and cdi/bdi facts if hidden products is present in layout
const isDisabledDueToStackedProducts = (rowColConfigs: RowColConfig[]) => {
    return rowColConfigs.some((rowColConfig) => rowColConfig.type === 'products' && rowColConfig.stack);
};

export function extractTypeValue(type: string) {
    const match = type.match(/^(customCharacteristics|characteristics|ppgs)_(.+)$/);
    return match ? match[2] : null;
}

export const getFilterKey = (option) => {
    let returnValue = '';
    if (isCharacteristicFilter(option.value) || isPpgFilter(option.value)) {
        returnValue = `${option.value.type}_${option.value.data.id || option.value.data}`;
    } else {
        returnValue = option.value.type || option.value;
    }
    return returnValue;
};

export const productsInitialGroups: DodSelectionPreviewNode[] = [
    {
        label: 'Hierarchy',
        filterType: DodProductParentFilterTypes.hierarchy,
        children: [
            { label: 'Department', filterType: DodProductFilterTypes.DEPARTMENTS },
            { label: 'Super Category', filterType: DodProductFilterTypes.SUPER_CATEGORIES },
            { label: 'Category', filterType: DodProductFilterTypes.CATEGORIES },
            { label: 'Subcategory', filterType: DodProductFilterTypes.SUBCATEGORIES },
            { label: 'UPC', filterType: DodProductFilterTypes.UPC },
            { label: 'Product Description', filterType: DodProductFilterTypes.PRODUCT_DESCRIPTIONS },
        ],
    },
    {
        label: 'Brand & Manufacturer',
        filterType: DodProductParentFilterTypes.brandAndManufacturer,
        children: [
            { label: 'Parent Company', filterType: DodProductFilterTypes.PARENT_COMPANIES },
            { label: 'Manufacturer', filterType: DodProductFilterTypes.MANUFACTURERS },
            { label: 'Brand', filterType: DodProductFilterTypes.BRANDS },
        ],
    },
    {
        label: 'Characteristics',
        filterType: DodProductParentFilterTypes.characteristics,
        loadChildren(filters) {
            return Object.entries(filters.characteristics).map(([characteristicDisplayValue, filter]) => ({
                label: filter.displayName,
                filterType: {
                    type: DodProductFilterTypes.CHARACTERISTICS,
                    data: { id: characteristicDisplayValue, displayName: filter.displayName },
                },
                values: filter.values,
                summedSelections: filter.summedSelections,
            }));
        },
    },
    {
        label: 'My Custom Characteristics',
        filterType: DodProductParentFilterTypes.customCharacteristics,
        loadChildren(filters) {
            return Object.entries(filters.customCharacteristics).map(([characteristicDisplayValue, filter]) => ({
                label: filter.displayName,
                filterType: {
                    type: DodProductFilterTypes.CUSTOM_CHARACTERISTICS,
                    data: { id: characteristicDisplayValue, displayName: filter.displayName },
                },
                values: filter.values,
                summedSelections: filter.summedSelections,
            }));
        },
    },
    {
        label: 'My PPGs',
        filterType: DodProductParentFilterTypes.ppgs,
        loadChildren(filters) {
            return Object.entries(filters.ppgs).map(([ppgDisplayValue, filter]) => ({
                label: filter.displayName,
                filterType: {
                    type: DodProductFilterTypes.PPGS,
                    data: { id: ppgDisplayValue, displayName: filter.displayName },
                },
                values: filter.values,
                summedSelections: filter.summedSelections,
            }));
        },
    },
];

// Market characteristics hierarchy order - determines consistent display order across all UI contexts
// IDs match the keys in DodCharacteristicFilterMap
export const MARKET_CHARACTERISTICS_HIERARCHY_ORDER_IDS = [
    'marketType',
    'marketLevel',
    'marketTier',
    'parentRetailer',
    'parentCompany',
    'channel',
    'childMarketType',
];

// Market characteristics hierarchy order with display labels for Excel layout preview
// Display names as they appear in the layout designer
export const MARKET_CHARACTERISTICS_HIERARCHY_ORDER_DISPLAY = [
    'Market Type',
    'Market Level',
    'National vs. Retail',
    'Parent Retailer',
    'Parent Company',
    'Channel',
    'Child Market Type',
];

export function getFilterType(filterType: DodFilterType | string | undefined): DodFilterType | undefined {
    if (typeof filterType === 'string') {
        return {
            type: filterType
        }
    }
    return filterType;
}

export const getCreatedDtm = (item: DodDeletedExtractsResponse) => {
    if (item.extractDelivery && item.type === 'Scheduled' && new Date(item.extractDelivery) > new Date()) {
        return 'NA';
    }
    return item.createdDtm ? format(new Date(item.createdDtm), 'yyyy-MM-dd') : 'NA';
};
export const getTimestamp = (item: DodDeletedExtractsResponse) => {
    if (item.extractDelivery && item.type === 'Scheduled' && new Date(item.extractDelivery) > new Date()) {
        return 'NA';
    }
    return item.createdDtm
        ? moment(item?.createdDtm).isValid()
            ? new Date(item?.createdDtm).toLocaleTimeString()
            : 'NA'
        : 'NA';
};

export const calculateDiffInDays = (dateString: string): number => {
    if (!dateString) {
        console.error('Invalid date string provided.');
        return NaN; // Return NaN for invalid input
    }

    const updated = new Date(dateString);
    if (isNaN(updated.getDate())) {
        console.error('Invalid date format:', dateString);
        return NaN; // Return NaN for invalid date format
    }

    const today = new Date();
    const diffTime = today.getDate() - updated.getDate();
    return diffTime;
};

export const mapPresetToRecommendedSelections = (
    presets: Partial<Record<DodPresetType, DodPresetSummary | undefined>> | RecommendedSelectionsMap,
    recommendedSelectionsList: Record<
        DodRecommendedSelectionsType,
        (RecommendedSelectionsSummary | RecommendedSelections)[]
    >
): { product: string; time_period: string; fact: string } => {
    // Helper function to find the display name for a given preset and recommended list
    const findRecommendedSelection = (preset: { id?: number }, recommendedList: { id: number; displayName: string }[]): string => {
        if (preset?.id && preset.id !== -1) {
            const match = recommendedList.find((item) => item.id === preset.id);
            return match?.displayName || '';
        }
        return '';
    };

    // Extract presets and recommended lists
    const presetProduct = presets?.product || {};
    const recommendedProduct = recommendedSelectionsList?.product || [];
    const presetTimePeriod = presets?.time_period || {};
    const recommendedTimePeriod = recommendedSelectionsList?.time_period || [];
    const presetFact = presets?.fact || {};
    const recommendedFact = recommendedSelectionsList?.fact || [];

    // Map presets to recommended selections
    const product = findRecommendedSelection(presetProduct, recommendedProduct);
    const time_period = findRecommendedSelection(presetTimePeriod, recommendedTimePeriod);
    const fact = findRecommendedSelection(presetFact, recommendedFact);

    return { product, time_period, fact };
};

export const isWorkInProgressFactSelected = (runConfig: DodRunConfig) => {
    let isWipFactSelected: boolean = false;
    let incompatibleFacts: string[] = [];
    if (
        Boolean(
            intersection(
                runConfig.facts.map((fact) => fact.id),
                weeksSellingFactIds
            ).length
        )
    ) {
        isWipFactSelected = true;
        incompatibleFacts = ['Weeks Selling'];
    }
    if (
        Boolean(
            intersection(
                runConfig.facts.map((fact) => fact.id),
                cdiBdiFactIds
            ).length
        )
    ) {
        isWipFactSelected = true;
        incompatibleFacts = ['CDI/BDI'];
    }
    if (
        Boolean(
            intersection(
                runConfig.facts.map((fact) => fact.id),
                weeksSellingFactIds
            ).length &&
                intersection(
                    runConfig.facts.map((fact) => fact.id),
                    cdiBdiFactIds
                ).length
        )
    ) {
        isWipFactSelected = true;
        incompatibleFacts = ['Weeks Selling', 'CDI/BDI'];
    }

    return { isWipFactSelected, incompatibleFacts };
};

// Function to check if any summedSelections length is greater than 0
export const hasSummedSelections = (obj: Partial<DodFilters>) => {
    for (const key in obj) {
        if (obj[key] && typeof obj[key] === 'object') {
            if (Array.isArray(obj[key].summedSelections) && obj[key].summedSelections.length > 0) {
                return true; // Found a summedSelections with length > 0
            }
            // Recursively check nested objects
            if (hasSummedSelections(obj[key])) {
                return true;
            }
        }
    }
    return false; // No summedSelections with length > 0 found
};